import React, { useCallback, useEffect, useRef, useState } from "react";
import type { FindChangedEvent, GridApi } from "ag-grid-community";

/**
 * SnGrid 찾기 바 (Ctrl+F)
 */

interface FindBarEntry {
  open: () => void;
}
const findBarRegistry = new Map<number, FindBarEntry>();
let lastInteractedFindBarId: number | null = null;
let findBarIdSeq = 0;

const nextFindBarId = () => ++findBarIdSeq;

const markFindBarInteracted = (id: number) => {
  lastInteractedFindBarId = id;
};

/**
 * Ctrl+F 처리: 마지막으로 조작한 그리드의 찾기 바를 연다.
 */
const openFindBarForInteractedGrid = (fallbackId: number) => {
  const targetId =
    lastInteractedFindBarId !== null &&
    findBarRegistry.has(lastInteractedFindBarId)
      ? lastInteractedFindBarId
      : fallbackId;
  findBarRegistry.get(targetId)?.open();
};

export const useSnGridFind = (
  gridApiRef: React.MutableRefObject<GridApi | null | undefined>
) => {
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextFindBarId();
  const id = idRef.current;

  const getApi = useCallback(() => gridApiRef.current, [gridApiRef]);

  // 찾기 바를 닫으면 마지막 포커스 셀로 포커스 복귀
  const onClose = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || api.isDestroyed()) return;
    const focused = api.getFocusedCell();
    if (focused) {
      api.setFocusedCell(focused.rowIndex, focused.column, focused.rowPinned);
    }
  }, [gridApiRef]);

  // Ctrl+F → 마지막 조작 그리드의 찾기 바 열기 / 그 외 키 → 이 그리드를 마지막 조작 그리드로 기록
  const onKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "f") {
      e.preventDefault(); // 브라우저 기본 찾기 차단
      e.stopPropagation();
      openFindBarForInteractedGrid(id);
      return;
    }
    if (["Control", "Shift", "Alt", "Meta"].includes(e.key)) return;
    markFindBarInteracted(id);
  };

  const onMouseDownCapture = () => markFindBarInteracted(id);

  return {
    wrapperProps: { onMouseDownCapture, onKeyDownCapture },
    barProps: { id, getApi, onClose },
  };
};

interface SnGridFindBarProps {
  /** 레지스트리 식별자 (SnGrid 인스턴스별 고유값) */
  id: number;
  /** 그리드 API 반환 함수 (gridReady 이후에만 값이 존재) */
  getApi: () => GridApi | null | undefined;
  /** 닫힐 때 호출 (그리드로 포커스 복귀 등) */
  onClose?: () => void;
  /** 입력 후 검색 반영까지의 지연(ms). 대량 데이터일 때 입력 렉 방지용 */
  debounceMs?: number;
}

const iconProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
const IconSearch = () => (
  <svg {...iconProps}>
    <circle cx="7" cy="7" r="4.25" />
    <path d="M10.25 10.25 13.5 13.5" />
  </svg>
);
const IconPrevious = () => (
  <svg {...iconProps}>
    <path d="M10 4 6 8l4 4" />
  </svg>
);
const IconNext = () => (
  <svg {...iconProps}>
    <path d="m6 4 4 4-4 4" />
  </svg>
);
const IconCross = () => (
  <svg {...iconProps}>
    <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" />
  </svg>
);

const SnGridFindBar = ({
  id,
  getApi,
  onClose,
  debounceMs = 150,
}: SnGridFindBarProps) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const [total, setTotal] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applySearch = useCallback(
    (value: string) => {
      const api = getApi();
      if (!api || api.isDestroyed()) return;
      api.setGridOption("findSearchValue", value || undefined);
      if (value) {
        // 검색값이 바뀌면 활성 매치가 초기화되므로 첫 매치로 이동(스크롤 포함)
        api.findNext();
      }
    },
    [getApi]
  );

  const clearSearch = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const api = getApi();
    if (api && !api.isDestroyed()) {
      api.setGridOption("findSearchValue", undefined);
    }
    setActive(0);
    setTotal(0);
  }, [getApi]);

  const focusInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  const doOpen = useCallback(() => {
    setOpen(true);
    focusInput();
  }, []);

  const doClose = () => {
    setOpen(false);
    setText("");
    clearSearch();
    onClose?.();
  };

  // 레지스트리 등록/해제
  useEffect(() => {
    findBarRegistry.set(id, { open: doOpen });
    return () => {
      findBarRegistry.delete(id);
      if (lastInteractedFindBarId === id) lastInteractedFindBarId = null;
    };
  }, [id, doOpen]);

  // 그리드의 findChanged 이벤트로 "n/전체" 표시 갱신
  useEffect(() => {
    if (!open) return;
    const api = getApi();
    if (!api || api.isDestroyed()) return;

    const listener = (e: FindChangedEvent) => {
      setTotal(e.totalMatches ?? 0);
      setActive(e.activeMatch?.numOverall ?? 0);
    };
    api.addEventListener("findChanged", listener);
    return () => {
      if (!api.isDestroyed()) {
        api.removeEventListener("findChanged", listener);
      }
    };
  }, [open, getApi]);

  // 언마운트 시 검색값 정리
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      const api = getApi();
      if (api && !api.isDestroyed()) {
        api.setGridOption("findSearchValue", undefined);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setText(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      applySearch(value);
    }, debounceMs);
  };

  const goNext = () => {
    const api = getApi();
    if (!api || api.isDestroyed() || !text) return;
    api.findNext();
  };

  const goPrevious = () => {
    const api = getApi();
    if (!api || api.isDestroyed() || !text) return;
    api.findPrevious();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        // 디바운스 대기 중이면 즉시 반영
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
          debounceTimer.current = null;
          applySearch(text);
          return;
        }
        if (e.shiftKey) goPrevious();
        else goNext();
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        doClose();
        break;
      case "F3":
        e.preventDefault();
        if (e.shiftKey) goPrevious();
        else goNext();
        break;
      default:
        break;
    }
  };

  if (!open) return null;

  const hasText = text.length > 0;
  const canNavigate = hasText && total > 0;

  return (
    <div
      className="sn-grid-find-toolbar ag-theme-alpine"
      role="toolbar"
      onMouseDown={(e) => {
        // 버튼/여백 클릭 시 입력창 포커스가 빠지지 않도록
        if ((e.target as HTMLElement).tagName !== "INPUT") {
          e.preventDefault();
        }
      }}
    >
      <div className="sn-grid-find" role="search">
        <span className="sn-grid-find-icon">
          <IconSearch />
        </span>
        <input
          ref={inputRef}
          type="text"
          className="sn-grid-find-field"
          placeholder="찾기..."
          aria-label="그리드에서 찾기"
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
        />
        {hasText && (
          <span className="sn-grid-find-match-count">
            {active}/{total}
          </span>
        )}
        <button
          type="button"
          className="sn-grid-find-button"
          title="이전 (Shift+Enter)"
          aria-label="이전 매치"
          disabled={!canNavigate}
          onClick={goPrevious}
        >
          <IconPrevious />
        </button>
        <button
          type="button"
          className="sn-grid-find-button"
          title="다음 (Enter)"
          aria-label="다음 매치"
          disabled={!canNavigate}
          onClick={goNext}
        >
          <IconNext />
        </button>
      </div>
      <button
        type="button"
        className="sn-grid-find-toolbar-button"
        title="닫기 (Esc)"
        aria-label="찾기 닫기"
        onClick={doClose}
      >
        <IconCross />
      </button>
    </div>
  );
};

export default SnGridFindBar;
